import { ChangeDetectionStrategy, Component, OnDestroy } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { GenericFilterItem } from '@frontend/common/util';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { LocationStatusApiService } from '@frontend/shared/location/data-access';
import { LocationStatus } from '@frontend/shared/location/data-access/src/lib/location-status/location-status.graphql';
import { BehaviorSubject } from 'rxjs';
import { combineLatest } from 'rxjs';
import { first, map, shareReplay, startWith } from 'rxjs/operators';
import { CareFiltersDialogService } from './care-filters-dialog.service';

@UntilDestroy()
@Component({
  selector: 'frontend-care-filters-dialog',
  templateUrl: './care-filters-dialog.component.html',
  styleUrls: ['./care-filters-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [CareFiltersDialogService],
  standalone: false
})
export class CareFiltersDialogComponent implements OnDestroy {
  locations: GenericFilterItem[];
  providers: GenericFilterItem[];
  appointment_types: GenericFilterItem[];
  workflows: GenericFilterItem[];
  phases: GenericFilterItem[];
  allPhases: GenericFilterItem[];
  current_department_id: number;
  location_groups: GenericFilterItem[];
  locationStatuses: LocationStatus[] = [];

  form: FormGroup = new FormGroup({
    locations: new FormControl([]),
    location_status: new FormControl([]),
    providers: new FormControl([]),
    appointment_types: new FormControl([]),
    workflows: new FormControl([]),
    phases: new FormControl([]),
    location_groups: new FormControl([]),
  });

  private statuses$ = this.locationStatusApi.getAll().pipe(first(), shareReplay(1));
  private contentReady$ = new BehaviorSubject<boolean>(false);
  loading$ = this.contentReady$.asObservable().pipe(
    map((ready) => !ready),
    startWith(true)
  );

  constructor(
    private dialogRef: MatDialogRef<CareFiltersDialogComponent>,
    private srv: CareFiltersDialogService,
    private locationStatusApi: LocationStatusApiService
  ) {
    combineLatest([this.srv.filters$, this.statuses$])
      .pipe(first(), untilDestroyed(this))
      .subscribe(([[options, preselected], statuses]) => {
        this.locations = options.locations;
        this.providers = options.providers;
        this.appointment_types = options.appointment_types;
        this.workflows = options.workflows;
        this.allPhases = this.phases = options.phases;
        this.location_groups = options.location_groups;
        this.locationStatuses = statuses;
        const location_groups = preselected?.location_groups?.filter((groupId) =>
          options.location_groups.some((group) => group.id === groupId)
        );
        if (preselected) {
          this.form.patchValue({ ...preselected, location_groups });
        }
        this.contentReady$.next(true);
      });
    this.form.controls.workflows.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe((selectedWorkflows: number[]) => {
        this.phases = this.allPhases?.filter(
          (w) => !selectedWorkflows?.length || selectedWorkflows.indexOf(w.id) > -1
        );
        const selectedPhases = this.form.controls.phases.value;
        const filteredPhases = selectedPhases?.filter((id) =>
          this.phases?.some((w) => w.children.map((p) => p.id).indexOf(id) > -1)
        );
        this.form.controls.phases.setValue(filteredPhases);
      });
  }

  ngOnDestroy() {}

  getStatusColor(statusId: number): string {
    const status = this.locationStatuses.find((s) => s.id === statusId);
    return status?.location_status_color || '';
  }

  save(data?) {
    this.form.disable();
    this.form.updateValueAndValidity();
    this.srv.saveCareFilters(data);
    this.dialogRef.close();
  }
}
